import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, distinctUntilChanged, map, Observable, startWith, Subject, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Response, SubscriptionDto } from '../interfaces';
import { SUBSCRIPTION_PLAN } from '../enums';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private subscriptionPlanGrade: SUBSCRIPTION_PLAN[] = [SUBSCRIPTION_PLAN.SMALL, SUBSCRIPTION_PLAN.MEDIUM];

  private reFetch$: Subject<void> = new Subject<void>();
  private subscriptions$: BehaviorSubject<SubscriptionDto[]> = new BehaviorSubject<SubscriptionDto[]>([]);

  constructor(private readonly http: HttpClient) {
    this.reFetch$
      .pipe(
        startWith(void 0),
        switchMap(() => this.getSubscriptionsRequest()),
        map((response) => {
          if (!response.success) {
            return [];
          }

          return response.data!;
        })
      )
      .subscribe((subscriptions) => {
        this.subscriptions$.next(subscriptions);
      })
  }

  reFetch() {
    this.reFetch$.next();
  }

  getSubscriptionByCompanyId(companyId: number): Observable<SubscriptionDto | null> {
    return this.subscriptions$.pipe(
      map((subscriptions) => {
        return subscriptions.find((sub) => sub.company.id === companyId) ?? null;
      }),
      distinctUntilChanged()
    );
  }

  compareSubscriptionPlans(planA: SUBSCRIPTION_PLAN, planB: SUBSCRIPTION_PLAN): number {
    return Math.sign(this.subscriptionPlanGrade.indexOf(planA) - this.subscriptionPlanGrade.indexOf(planB));
  }

  initPaymentRequest(plan: SUBSCRIPTION_PLAN, redirectUrl: string): Observable<Response<string>> {
    return this.http.post<Response<string>>(`${environment.baseUrl}/payments/init`, { plan, redirectUrl });
  }

  updateSubscriptionRequest(plan: SUBSCRIPTION_PLAN): Observable<Response<void>> {
    return this.http.put<Response<void>>(`${environment.baseUrl}/payments`, {}, { params: { plan } });
  }

  unsubscribeRequest(): Observable<Response<void>> {
    return this.http.post<Response<void>>(`${environment.baseUrl}/payments/unsubscribe`, {});
  }

  sendReceiptRequest(paymentId: string): Observable<Response<void>> {
    return this.http.get<Response<void>>(`${environment.baseUrl}/payments/ticket`, { params: { paymentId } });
  }

  getSubscriptionsRequest(): Observable<Response<SubscriptionDto[]>> {
    return this.http.get<Response<SubscriptionDto[]>>(`${environment.baseUrl}/payments`);
  }
}
